/**
 * Compatibility shim. The live i18n instance lives in `src/i18n.ts`, which
 * code-splits every non-English locale. This module only re-exports it plus the
 * locale display metadata, so no bundle ever eagerly pulls in all five JSON files.
 */
import i18n, {
  SUPPORTED_LOCALES,
  ensureLocaleLoaded,
  getLocaleFromPath,
  type SupportedLocale,
} from '../i18n';

export { SUPPORTED_LOCALES, ensureLocaleLoaded, getLocaleFromPath };
export type { SupportedLocale };

export interface LocaleDisplayInfo {
  country: string;
  language: string;
  currencySymbol: string;
  shortCode: string;
  flag: string;
}

/**
 * Language, not country. `country` and `flag` are kept because a few surfaces
 * still render them, but nothing user-facing should present a locale as a
 * place: the site offers five languages, not nine markets. `shortCode` is the
 * compact switcher label.
 */
export const LOCALE_DISPLAY: Record<SupportedLocale, LocaleDisplayInfo> = {
  'en-US': { country: 'United States', language: 'English', currencySymbol: '$', shortCode: 'EN', flag: '🇺🇸' },
  'fr-FR': { country: 'France', language: 'Français', currencySymbol: '€', shortCode: 'FR', flag: '🇫🇷' },
  'es-MX': { country: 'México', language: 'Español', currencySymbol: '$', shortCode: 'ES', flag: '🇲🇽' },
  'de-DE': { country: 'Deutschland', language: 'Deutsch', currencySymbol: '€', shortCode: 'DE', flag: '🇩🇪' },
  'ja-JP': { country: '日本', language: '日本語', currencySymbol: '¥', shortCode: 'JA', flag: '🇯🇵' },
};

// Backward compat. Language only: the old form read "United States, English ($)".
export const LOCALE_NAMES: Record<SupportedLocale, string> = Object.fromEntries(
  SUPPORTED_LOCALES.map(l => [l, LOCALE_DISPLAY[l].language])
) as Record<SupportedLocale, string>;

export default i18n;
